import { useEffect, useState } from "react";
import api from "@/lib/api";
import MarketingAI from "@/components/admin/MarketingAI";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, Eye, Target, TrendingUp, Globe, Smartphone, RefreshCw, ExternalLink } from "lucide-react";

const COLORS = ["#2563eb", "#0ea5e9", "#22c55e", "#f59e0b", "#ec4899", "#8b5cf6", "#14b8a6", "#ef4444"];
const TURO_SOURCE_LABELS = {
  hero_badge: "Home — hero badge",
  home_airport_qr: "Home — airport QR",
  home_airport_link: "Home — airport link",
  airport_hero: "Airport page — hero",
  airport_qr_card: "Airport page — QR card",
  airport_cta: "Airport page — CTA",
  qr_download: "QR code downloaded",
  other: "Other",
};
const RANGES = [
  { d: 7, label: "7 days" },
  { d: 30, label: "30 days" },
  { d: 90, label: "90 days" },
];

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Icon size={16} /> {label}
      </div>
      <div className="mt-2 font-heading text-3xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function fmtDate(s) {
  try { return new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch (e) { return s; }
}
function fmtTime(s) {
  try { return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch (e) { return s; }
}

export default function WebAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (d) => {
    setLoading(true);
    api.get(`/admin/analytics/web?days=${d}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(days); }, [days]);

  const t = data?.totals || {};
  const channels = data?.by_channel || [];
  const turo = data?.turo_referrals || { total: 0, by_source: [] };

  return (
    <div data-testid="web-analytics" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Website Analytics</h2>
          <p className="text-sm text-slate-500">Traffic, lead sources &amp; an AI growth strategist for belorentals.com</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.d}
                data-testid={`web-range-${r.d}`}
                onClick={() => setDays(r.d)}
                className={`px-3.5 py-2 text-sm font-medium transition-colors ${days === r.d ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={() => load(days)} className="grid place-items-center w-10 h-10 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50" title="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-visitors" icon={Users} label="Visitors" value={t.visitors ?? 0} sub={`${t.pages_per_session ?? 0} pages / visit`} />
        <Kpi testid="kpi-pageviews" icon={Eye} label="Pageviews" value={t.pageviews ?? 0} />
        <Kpi testid="kpi-leads" icon={Target} label="Leads" value={t.leads ?? 0} sub={`${t.contacts ?? 0} contact · ${t.bookings ?? 0} booking`} />
        <Kpi testid="kpi-conversion" icon={TrendingUp} label="Conversion" value={`${t.conversion_rate ?? 0}%`} sub="visitor → lead" />
      </div>

      {(!loading && t.visitors === 0) && (
        <div className="text-sm text-slate-500 bg-blue-50 border border-blue-100 rounded-xl p-4">
          No traffic recorded yet for this range. Data fills in automatically as visitors browse the live site
          (belorentals.com). Add <code className="text-blue-700">?utm_source=…&amp;utm_medium=…&amp;utm_campaign=…</code> to your ad/social links to track campaigns.
        </div>
      )}

      {/* Trend + channels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Visitors &amp; leads over time</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data?.timeseries || []}>
              <defs>
                <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} />
              <Tooltip labelFormatter={fmtDate} />
              <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#2563eb" strokeWidth={2} fill="url(#gv)" />
              <Area type="monotone" dataKey="leads" name="Leads" stroke="#22c55e" strokeWidth={2} fillOpacity={0} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-1">Where visitors come from</h3>
          <p className="text-xs text-slate-400 mb-2">Acquisition channel (first touch)</p>
          {channels.length ? (
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie data={channels} dataKey="visitors" nameKey="channel" cx="50%" cy="50%" outerRadius={75} innerRadius={45} paddingAngle={2}>
                  {channels.map((c, i) => <Cell key={c.channel} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-10 text-center">No data yet.</p>}
        </div>
      </div>

      {/* Turo storefront referrals */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="turo-referrals-card">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><ExternalLink size={16} className="text-blue-600" /> Turo storefront referrals</h3>
            <span className="font-heading text-2xl font-bold text-blue-600" data-testid="turo-referrals-total">{turo.total}</span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">Click-throughs from belorentals.com to your Turo storefront (last {days} days)</p>
          {turo.by_source.length ? (
            <div className="mt-4 space-y-2.5">
              {turo.by_source.map((s, i) => {
                const pct = turo.total ? Math.round((100 * s.count) / turo.total) : 0;
                return (
                  <div key={s.source} data-testid={`turo-source-${s.source}`}>
                    <div className="flex justify-between text-sm text-slate-600 mb-1"><span>{TURO_SOURCE_LABELS[s.source] || s.source}</span><span className="font-semibold text-slate-900">{s.count}</span></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 mt-4">No Turo click-throughs yet. Links on the home hero badge, the home airport section, and the /logan-airport-drop-off page are all tracked here.</p>
          )}
        </div>

        {/* Lead sources + top pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Lead sources</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="pb-2 font-medium">Channel</th><th className="pb-2 font-medium text-right">Visitors</th><th className="pb-2 font-medium text-right">Leads</th><th className="pb-2 font-medium text-right">Conv.</th>
            </tr></thead>
            <tbody>
              {channels.length ? channels.map((c) => (
                <tr key={c.channel} className="border-b border-slate-50">
                  <td className="py-2 text-slate-700">{c.channel}</td>
                  <td className="py-2 text-right text-slate-600">{c.visitors}</td>
                  <td className="py-2 text-right font-semibold text-slate-900">{c.leads}</td>
                  <td className="py-2 text-right text-slate-500">{c.visitors ? Math.round((100 * c.leads) / c.visitors) : 0}%</td>
                </tr>
              )) : <tr><td colSpan={4} className="py-6 text-center text-slate-400">No data yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Top pages</h3>
          <div className="space-y-2">
            {(data?.top_pages || []).length ? (data?.top_pages || []).map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 truncate mr-3">{p.path}</span>
                <span className="text-slate-500 shrink-0">{p.views}</span>
              </div>
            )) : <p className="text-sm text-slate-400">No data yet.</p>}
          </div>
        </div>
      </div>

      {/* Devices + referrers + recent leads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Smartphone size={16} /> Devices</h3>
          {(data?.devices || []).length ? (data?.devices || []).map((d, i) => {
            const total = (data.devices).reduce((a, b) => a + b.count, 0) || 1;
            const pct = Math.round((100 * d.count) / total);
            return (
              <div key={d.device} className="mb-3">
                <div className="flex justify-between text-sm text-slate-600 mb-1"><span>{d.device}</span><span>{pct}%</span></div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                </div>
              </div>
            );
          }) : <p className="text-sm text-slate-400">No data yet.</p>}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Globe size={16} /> Top referrers</h3>
          <div className="space-y-2">
            {(data?.top_referrers || []).length ? (data?.top_referrers || []).map((r) => (
              <div key={r.referrer} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 truncate mr-3">{r.referrer}</span>
                <span className="text-slate-500 shrink-0">{r.count}</span>
              </div>
            )) : <p className="text-sm text-slate-400">Direct traffic only so far.</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Recent leads</h3>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {(data?.recent_leads || []).length ? (data?.recent_leads || []).map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                <div className="min-w-0">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mr-2 ${l.type === "booking" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                    {l.type === "booking" ? "Booking" : "Contact"}
                  </span>
                  <span className="text-slate-500">{l.channel}</span>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{fmtTime(l.ts)}</span>
              </div>
            )) : <p className="text-sm text-slate-400">No leads captured yet.</p>}
          </div>
        </div>
      </div>

      <MarketingAI days={days} />
    </div>
  );
}
